import { buildChemistryAnalysisCopy } from '../chemistryAnalysisCopy';
import {
  getChemistrySequenceDirection,
  getChemistrySequenceMetrics,
  getProgressValue,
} from '../chemistryAnalysisUtils';
import {
  CHEMISTRY_VIDEO_TIMELINE,
  getChemistryVideoScene,
  getChemistryVideoSceneProgress,
} from './chemistryVideoLayout';
import {
  drawCoverImage,
  drawFallbackPortrait,
  drawWrappedText,
  easeOutCubic,
  fillRoundedRect,
  roundedRectPath,
  setCanvasFont,
} from './chemistryVideoUtils';

const COLORS = {
  background: '#090812',
  panel: 'rgba(23, 22, 43, 0.92)',
  text: '#ffffff',
  muted: '#b8b5cd',
  pink: '#ff6b8a',
  blue: '#818cf8',
  mint: '#5eead4',
};

export function createChemistryVideoRenderModel({ result, nameA, nameB, language, t, labels }) {
  return {
    result,
    nameA: nameA || 'A',
    nameB: nameB || 'B',
    copy: buildChemistryAnalysisCopy({ result, nameA, nameB, language, t }),
    metrics: getChemistrySequenceMetrics(result, language),
    direction: getChemistrySequenceDirection(result, nameA, nameB),
    labels,
  };
}

function drawBackground(ctx) {
  const gradient = ctx.createLinearGradient(0, 0, 720, 1280);
  gradient.addColorStop(0, '#090812');
  gradient.addColorStop(0.55, '#171329');
  gradient.addColorStop(1, '#090812');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 720, 1280);
  const glow = ctx.createRadialGradient(580, 160, 10, 580, 160, 480);
  glow.addColorStop(0, 'rgba(129, 140, 248, 0.2)');
  glow.addColorStop(1, 'rgba(129, 140, 248, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 720, 1280);
}

function drawBrand(ctx, progress) {
  ctx.save();
  ctx.globalAlpha = 0.72 + (0.28 * easeOutCubic(progress));
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 28, 800);
  ctx.fillText('STARTUS', 48, 62);
  ctx.fillStyle = COLORS.pink;
  ctx.fillRect(48, 76, 48, 4);
  ctx.restore();
}

function drawPortrait(ctx, image, name, x, y, width, height, accent, focus, overlay, overlayOpacity = 0) {
  ctx.save();
  roundedRectPath(ctx, x, y, width, height, 38);
  ctx.clip();
  if (!drawCoverImage(ctx, image, x, y, width, height, focus)) {
    drawFallbackPortrait(ctx, name, x, y, width, height, accent);
  }
  if (overlay && overlayOpacity > 0) {
    ctx.globalAlpha = overlayOpacity;
    drawCoverImage(ctx, overlay, x, y, width, height, focus);
    ctx.globalAlpha = 1;
  }
  const shade = ctx.createLinearGradient(0, y + (height * 0.5), 0, y + height);
  shade.addColorStop(0, 'rgba(8, 8, 18, 0)');
  shade.addColorStop(1, 'rgba(8, 8, 18, 0.88)');
  ctx.fillStyle = shade;
  ctx.fillRect(x, y, width, height);
  ctx.restore();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 4;
  roundedRectPath(ctx, x, y, width, height, 38);
  ctx.stroke();
}

function drawPortraitName(ctx, name, x, y, width) {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 30, 700);
  ctx.fillText(String(name || '').slice(0, 20), x + (width / 2), y);
}

function drawIntro(ctx, progress, assets, model) {
  ctx.textAlign = 'center';
  ctx.fillStyle = COLORS.muted;
  setCanvasFont(ctx, 24, 700);
  ctx.fillText(model.copy.intro.eyebrow, 360, 172);
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 52, 850);
  ctx.fillText(model.labels.brandTitle, 360, 246);

  const lift = 18 * (1 - easeOutCubic(progress));
  drawPortrait(ctx, assets.portraitA, model.nameA, 76, 342 + lift, 260, 430, COLORS.pink);
  drawPortrait(ctx, assets.portraitB, model.nameB, 384, 342 + lift, 260, 430, COLORS.blue);
  drawPortraitName(ctx, model.nameA, 76, 824 + lift, 260);
  drawPortraitName(ctx, model.nameB, 384, 824 + lift, 260);
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 40, 800);
  ctx.fillText('×', 360, 574 + lift);
  ctx.fillStyle = COLORS.muted;
  setCanvasFont(ctx, 30, 600);
  ctx.fillText(model.copy.intro.title, 360, 936);
}

function drawScan(ctx, progress, assets, model, person) {
  const isA = person === 'a';
  const name = isA ? model.nameA : model.nameB;
  const portrait = isA ? assets.portraitA : assets.portraitB;
  const mesh = isA ? assets.meshA : assets.meshB;
  const accent = isA ? COLORS.pink : COLORS.blue;
  const copy = isA ? model.copy.scanA : model.copy.scanB;
  ctx.textAlign = 'left';
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 42, 800);
  ctx.fillText(copy.title, 70, 150);

  drawPortrait(ctx, portrait, name, 70, 205, 580, 720, accent, null, mesh, mesh ? 0.66 : 0);
  const scanY = 230 + (660 * progress);
  const scanGlow = ctx.createLinearGradient(70, scanY, 650, scanY);
  scanGlow.addColorStop(0, 'rgba(94,234,212,0)');
  scanGlow.addColorStop(0.5, 'rgba(94,234,212,1)');
  scanGlow.addColorStop(1, 'rgba(94,234,212,0)');
  ctx.fillStyle = scanGlow;
  ctx.fillRect(70, scanY, 580, 5);
  ctx.shadowColor = COLORS.mint;
  ctx.shadowBlur = 24;
  ctx.fillRect(130, scanY, 460, 2);
  ctx.shadowBlur = 0;

  const shown = Math.max(1, Math.ceil(progress * copy.items.length));
  copy.items.forEach((item, index) => {
    const x = 70 + (index * 194);
    fillRoundedRect(ctx, x, 984, 174, 74, 24, index < shown ? `${accent}33` : 'rgba(255,255,255,0.06)');
    ctx.fillStyle = index < shown ? COLORS.text : COLORS.muted;
    ctx.textAlign = 'center';
    setCanvasFont(ctx, 22, 650);
    ctx.fillText(item, x + 87, 1030);
  });
  ctx.fillStyle = COLORS.muted;
  ctx.textAlign = 'center';
  setCanvasFont(ctx, 22, 600);
  ctx.fillText(model.labels.faceSignal, 360, 1140);
}

function drawMetricCard(ctx, label, body, value, x, y, accent) {
  fillRoundedRect(ctx, x, y, 580, 214, 32, COLORS.panel);
  ctx.fillStyle = accent;
  ctx.textAlign = 'left';
  setCanvasFont(ctx, 25, 750);
  ctx.fillText(label, x + 32, y + 52);
  ctx.fillStyle = COLORS.muted;
  setCanvasFont(ctx, 21, 500);
  ctx.fillText(body, x + 32, y + 92);
  ctx.fillStyle = COLORS.text;
  ctx.textAlign = 'right';
  setCanvasFont(ctx, 74, 850);
  ctx.fillText(String(value), x + 532, y + 158);
  setCanvasFont(ctx, 24, 650);
  ctx.fillStyle = COLORS.muted;
  ctx.fillText('/ 100', x + 532, y + 190);
}

function drawCompare(ctx, elapsedMs, progress, assets, model, scene) {
  ctx.fillStyle = COLORS.text;
  ctx.textAlign = 'center';
  setCanvasFont(ctx, 44, 800);
  ctx.fillText(model.labels.compareTitle, 360, 160);
  drawPortrait(ctx, assets.portraitA, model.nameA, 176, 208, 160, 200, COLORS.pink);
  drawPortrait(ctx, assets.portraitB, model.nameB, 384, 208, 160, 200, COLORS.blue);
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 28, 800);
  ctx.fillText('×', 360, 318);
  const comfort = getProgressValue(elapsedMs, scene.start, scene.end, model.metrics.comfortScore);
  const attraction = getProgressValue(elapsedMs, scene.start, scene.end, model.metrics.attractionScore);
  drawMetricCard(ctx, model.copy.compare.comfort, model.copy.compare.comfortLine, comfort, 70, 478, COLORS.mint);
  drawMetricCard(ctx, model.copy.compare.attraction, model.copy.compare.attractionLine, attraction, 70, 728, COLORS.pink);
  ctx.fillStyle = COLORS.muted;
  setCanvasFont(ctx, 22, 600);
  ctx.fillText(`${Math.round(progress * 100)}%`, 360, 1070);
}

function drawFlowBar(ctx, model) {
  const aScore = model.direction.flow.a_to_b;
  const bScore = model.direction.flow.b_to_a;
  const total = Math.max(aScore + bScore, 1);
  const aWidth = 580 * (aScore / total);
  fillRoundedRect(ctx, 70, 780, 580, 24, 12, 'rgba(255,255,255,0.1)');
  fillRoundedRect(ctx, 70, 780, aWidth, 24, 12, COLORS.pink);
  ctx.fillStyle = COLORS.text;
  ctx.textAlign = 'left';
  setCanvasFont(ctx, 24, 700);
  ctx.fillText(`${model.nameA} ${aScore}`, 70, 850);
  ctx.textAlign = 'right';
  ctx.fillText(`${model.nameB} ${bScore}`, 650, 850);
}

function drawDirection(ctx, progress, assets, model) {
  ctx.fillStyle = COLORS.muted;
  ctx.textAlign = 'center';
  setCanvasFont(ctx, 25, 700);
  ctx.fillText(model.copy.direction.title, 360, 154);
  drawPortrait(ctx, assets.portraitA, model.nameA, 82, 230, 240, 330, COLORS.pink);
  drawPortrait(ctx, assets.portraitB, model.nameB, 398, 230, 240, 330, COLORS.blue);
  const arrowOffset = 18 * Math.sin(progress * Math.PI);
  ctx.fillStyle = model.direction.lead === 'b' ? COLORS.blue : model.direction.lead === 'a' ? COLORS.pink : COLORS.mint;
  ctx.textAlign = 'center';
  setCanvasFont(ctx, 54, 800);
  ctx.fillText(model.direction.lead === 'b' ? '←' : model.direction.lead === 'a' ? '→' : '↔', 360 + arrowOffset, 420);
  drawFlowBar(ctx, model);
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 34, 800);
  drawWrappedText(ctx, model.copy.direction.summary, 360, 940, 580, 48, 3);
}

function drawScore(ctx, elapsedMs, assets, model, scene) {
  const score = getProgressValue(elapsedMs, scene.start, scene.end, model.metrics.chemistryScore);
  ctx.fillStyle = COLORS.muted;
  ctx.textAlign = 'center';
  setCanvasFont(ctx, 26, 700);
  ctx.fillText(model.copy.score.title, 360, 160);
  drawPortrait(ctx, assets.portraitA, model.nameA, 174, 214, 170, 220, COLORS.pink);
  drawPortrait(ctx, assets.portraitB, model.nameB, 376, 214, 170, 220, COLORS.blue);
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 136, 900);
  ctx.fillText(String(score), 360, 650);
  ctx.fillStyle = COLORS.muted;
  setCanvasFont(ctx, 28, 650);
  ctx.fillText(model.copy.score.outOf100, 360, 704);
  fillRoundedRect(ctx, 238, 758, 244, 82, 41, 'rgba(255,107,138,0.18)');
  ctx.fillStyle = COLORS.pink;
  setCanvasFont(ctx, 31, 850);
  ctx.fillText(`${model.metrics.grade.code} · ${model.metrics.grade.label}`, 360, 810);
  ctx.fillStyle = COLORS.text;
  setCanvasFont(ctx, 29, 750);
  drawWrappedText(ctx, model.result?.title || model.metrics.grade.interpretation, 360, 920, 570, 42, 3);
}

function drawOutro(ctx, progress, assets, model) {
  const scale = 0.96 + (0.04 * easeOutCubic(progress));
  ctx.save();
  ctx.translate(360, 520);
  ctx.scale(scale, scale);
  drawPortrait(ctx, assets.portraitA, model.nameA, -258, -300, 230, 330, COLORS.pink);
  drawPortrait(ctx, assets.portraitB, model.nameB, 28, -300, 230, 330, COLORS.blue);
  ctx.restore();
  ctx.fillStyle = COLORS.text;
  ctx.textAlign = 'center';
  setCanvasFont(ctx, 44, 850);
  drawWrappedText(ctx, model.labels.outroPrompt, 360, 670, 600, 60, 2);
  ctx.fillStyle = COLORS.pink;
  setCanvasFont(ctx, 56, 900);
  ctx.fillText('STARTUS', 360, 900);
  ctx.fillStyle = COLORS.muted;
  setCanvasFont(ctx, 28, 650);
  ctx.fillText('start-us.com', 360, 962);
}

export function renderChemistryVideoFrame(ctx, elapsedMs, { assets, model }) {
  if (!ctx) throw new Error('canvas_context_missing');
  const scene = getChemistryVideoScene(elapsedMs);
  const progress = getChemistryVideoSceneProgress(elapsedMs, scene);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  drawBackground(ctx);
  drawBrand(ctx, progress);
  if (scene.id === 'intro') drawIntro(ctx, progress, assets, model);
  else if (scene.id === 'scanA') drawScan(ctx, progress, assets, model, 'a');
  else if (scene.id === 'scanB') drawScan(ctx, progress, assets, model, 'b');
  else if (scene.id === 'compare') drawCompare(ctx, elapsedMs, progress, assets, model, scene);
  else if (scene.id === 'direction') drawDirection(ctx, progress, assets, model);
  else if (scene.id === 'score') drawScore(ctx, elapsedMs, assets, model, scene);
  else drawOutro(ctx, progress, assets, model);

  const timelineIndex = CHEMISTRY_VIDEO_TIMELINE.findIndex((item) => item.id === scene.id);
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fillRect(48, 1218, 624, 5);
  ctx.fillStyle = COLORS.pink;
  const totalProgress = (timelineIndex + progress) / CHEMISTRY_VIDEO_TIMELINE.length;
  ctx.fillRect(48, 1218, 624 * totalProgress, 5);
}
